import React, { useRef, useCallback, useMemo } from 'react';
import StandardsSection from './StandardsSection/StandardsSection';
import './LessonPlanDocument.css';

// Default template for backward compatibility and fallback
// Header placeholders are always rendered by the fixed header layout.
// Templates only define body sections below the header.
const HEADER_PLACEHOLDERS = [
  { name: 'title', type: 'text' },
  { name: 'grade_level', type: 'text' },
  { name: 'duration', type: 'text' },
  { name: 'subject', type: 'text' },
];

const DEFAULT_TEMPLATE = {
  content: `## Learning Objectives
<!-- type: list -->
{{ objectives }}

## Materials Needed
<!-- type: list -->
{{ materials }}

## Lesson Procedure
<!-- type: ordered_list -->
{{ procedure }}

## Assessment / Evaluation
<!-- type: text -->
{{ assessment }}

## Differentiation Strategies
<!-- type: text -->
{{ differentiation }}

## Notes / Extensions
<!-- type: text -->
{{ notes }}`,
  placeholders: [
    ...HEADER_PLACEHOLDERS,
    { name: 'objectives', type: 'list' },
    { name: 'materials', type: 'list' },
    { name: 'procedure', type: 'ordered_list' },
    { name: 'assessment', type: 'text' },
    { name: 'differentiation', type: 'text' },
    { name: 'notes', type: 'text' },
  ],
};

// Map old lessonPlan keys to template placeholder names
const LEGACY_KEY_MAP = {
  gradeLevel: 'grade_level',
};

function legacyToSections(lessonPlan) {
  if (!lessonPlan) return {};
  const sections = {};
  for (const [key, value] of Object.entries(lessonPlan)) {
    const mappedKey = LEGACY_KEY_MAP[key] || key;
    sections[mappedKey] = value;
  }
  return sections;
}

/**
 * Parse a single line into segments of static text and placeholders.
 */
function parseLineSegments(line, placeholderMap) {
  const segments = [];
  const regex = /\{\{\s*(\w+)\s*\}\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'static', text: line.slice(lastIndex, match.index) });
    }
    const name = match[1];
    segments.push({
      type: 'placeholder',
      text: match[0],
      placeholder: placeholderMap[name] || { name, type: 'text' },
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < line.length) {
    segments.push({ type: 'static', text: line.slice(lastIndex) });
  }

  return segments;
}

/**
 * Parse template content into line-based blocks.
 * Each line becomes an object with a lineType and segments.
 */
export function parseTemplateToBlocks(content, placeholders) {
  const placeholderMap = {};
  placeholders.forEach(p => { placeholderMap[p.name] = p; });

  // Strip YAML frontmatter (--- ... ---)
  let stripped = content;
  if (stripped.trimStart().startsWith('---')) {
    const afterFirst = stripped.indexOf('---') + 3;
    const closingIdx = stripped.indexOf('---', afterFirst);
    if (closingIdx !== -1) {
      stripped = stripped.slice(closingIdx + 3);
    }
  }

  const rawLines = stripped.split('\n');
  const result = [];

  for (const rawLine of rawLines) {
    const trimmed = rawLine.trim();
    if (!trimmed) continue;
    // Skip HTML comment lines (type hints)
    if (/^<!--.*?-->$/.test(trimmed)) continue;

    const segments = parseLineSegments(trimmed, placeholderMap);

    // Determine line type
    if (/^#\s/.test(trimmed) || trimmed === '#') {
      result.push({ lineType: 'h1', segments });
    } else if (/^##\s/.test(trimmed)) {
      result.push({ lineType: 'h2', text: trimmed.slice(3), segments });
    } else if (/^###\s/.test(trimmed)) {
      result.push({ lineType: 'h3', text: trimmed.slice(4), segments });
    } else if (
      segments.length === 1 &&
      segments[0].type === 'placeholder' &&
      ['list', 'ordered_list'].includes(segments[0].placeholder.type)
    ) {
      result.push({ lineType: 'section-body', segments });
    } else if (
      segments.length === 1 &&
      segments[0].type === 'placeholder'
    ) {
      result.push({ lineType: 'section-body', segments });
    } else {
      result.push({ lineType: 'inline', segments });
    }
  }

  return result;
}

/**
 * Render inline markdown (bold) as React elements.
 */
function renderInlineMarkdown(text) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

const LessonPlanDocument = ({
  // New template-based props
  template,
  sections: sectionsProp,
  // Legacy prop (backward compat)
  lessonPlan,
  // Common props
  onUpdate,
  validationErrors = {},
  lessonId,
  lessonStandards = [],
  onAddStandard,
  onRemoveStandard,
}) => {
  const documentRef = useRef(null);

  // Resolve template and sections (support both old and new APIs)
  const resolvedTemplate = template || DEFAULT_TEMPLATE;
  const sections = sectionsProp || (lessonPlan ? legacyToSections(lessonPlan) : {});

  const blocks = useMemo(
    () => parseTemplateToBlocks(resolvedTemplate.content, resolvedTemplate.placeholders),
    [resolvedTemplate]
  );

  // Build a set of placeholder names for standards insertion heuristic
  const placeholderNames = useMemo(
    () => resolvedTemplate.placeholders.map(p => p.name),
    [resolvedTemplate]
  );

  const handleContentChange = useCallback((section, value) => {
    // Map back to legacy key if the consumer uses old lessonPlan pattern
    if (lessonPlan && !sectionsProp) {
      const reverseMap = {};
      for (const [oldKey, newKey] of Object.entries(LEGACY_KEY_MAP)) {
        reverseMap[newKey] = oldKey;
      }
      const legacyKey = reverseMap[section] || section;
      onUpdate(legacyKey, value);
    } else {
      onUpdate(section, value);
    }
  }, [onUpdate, lessonPlan, sectionsProp]);

  const handleArrayItemChange = useCallback((section, index, value) => {
    const arr = Array.isArray(sections[section]) ? sections[section] : [];
    const newArray = [...arr];
    newArray[index] = value;
    handleContentChange(section, newArray);
  }, [sections, handleContentChange]);

  const handleAddArrayItem = useCallback((section, defaultValue = '') => {
    const arr = Array.isArray(sections[section]) ? sections[section] : [];
    handleContentChange(section, [...arr, defaultValue]);
  }, [sections, handleContentChange]);

  const handleRemoveArrayItem = useCallback((section, index) => {
    const arr = Array.isArray(sections[section]) ? sections[section] : [];
    const newArray = [...arr];
    newArray.splice(index, 1);
    handleContentChange(section, newArray);
  }, [sections, handleContentChange]);

  // Editable text component
  const EditableText = ({ value, onChange, placeholder, multiline = false, className = '' }) => {
    const handleBlur = (e) => {
      onChange(e.target.innerText);
    };

    if (multiline) {
      return (
        <div
          contentEditable
          suppressContentEditableWarning
          onBlur={handleBlur}
          className={`editable-field ${className}`}
          data-placeholder={placeholder}
        >
          {value}
        </div>
      );
    }

    return (
      <span
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        className={`editable-field editable-inline ${className}`}
        data-placeholder={placeholder}
      >
        {value}
      </span>
    );
  };

  // Render a placeholder based on its type
  const renderPlaceholder = (placeholder, key) => {
    const { name, type } = placeholder;
    const value = sections[name];

    // List sections
    if (type === 'list') {
      const raw = value;
      const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const label = name.replace(/_/g, ' ');
      const singularLabel = label.replace(/s$/, '');

      return (
        <div key={key} className="document-section">
          {items.length === 0 ? (
            <p className="section-placeholder">{label} will appear here...</p>
          ) : (
            <ul className="section-list">
              {items.map((item, index) => (
                <li key={index} className="list-item">
                  <EditableText
                    value={typeof item === 'object' ? JSON.stringify(item) : item}
                    onChange={(v) => handleArrayItemChange(name, index, v)}
                    placeholder={`Enter ${singularLabel}`}
                  />
                  <button
                    className="remove-item-btn no-print"
                    onClick={() => handleRemoveArrayItem(name, index)}
                    title="Remove item"
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            className="add-item-btn no-print"
            onClick={() => handleAddArrayItem(name, '')}
          >
            + Add {singularLabel}
          </button>
        </div>
      );
    }

    // Ordered list sections
    if (type === 'ordered_list') {
      const items = Array.isArray(value) ? value : [];

      return (
        <div key={key} className="document-section">
          {items.length === 0 ? (
            <p className="section-placeholder">Steps will appear here...</p>
          ) : (
            <ol className="procedure-list">
              {items.map((item, index) => (
                <li key={index} className="procedure-item">
                  <div className="procedure-content">
                    <EditableText
                      value={typeof item === 'object' ? item.description || item.step || JSON.stringify(item) : item}
                      onChange={(v) => handleArrayItemChange(name, index, v)}
                      placeholder="Enter step description"
                      multiline
                    />
                  </div>
                  <button
                    className="remove-item-btn no-print"
                    onClick={() => handleRemoveArrayItem(name, index)}
                    title="Remove step"
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ol>
          )}
          <button
            className="add-item-btn no-print"
            onClick={() => handleAddArrayItem(name, '')}
          >
            + Add Step
          </button>
        </div>
      );
    }

    // Default: text section
    return (
      <div key={key} className="document-section">
        <EditableText
          value={value || ''}
          onChange={(v) => handleContentChange(name, v)}
          placeholder={`Enter ${name.replace(/_/g, ' ')}...`}
          multiline
          className="text-area"
        />
      </div>
    );
  };

  // Render an inline segment (static text or inline editable placeholder)
  const renderInlineSegment = (segment, key) => {
    if (segment.type === 'static') {
      return <React.Fragment key={key}>{renderInlineMarkdown(segment.text)}</React.Fragment>;
    }
    // Inline placeholder — render as editable span
    const { name } = segment.placeholder;
    const value = sections[name];
    const errorKey = name === 'subject' ? 'subject' : name;
    return (
      <EditableText
        key={key}
        value={value != null ? String(value) : ''}
        onChange={(v) => handleContentChange(name, v)}
        placeholder={`Enter ${name.replace(/_/g, ' ')}`}
        className={validationErrors[errorKey] ? 'field-error' : ''}
      />
    );
  };

  // Determine where to insert standards section
  const standardsInsertAfter = useMemo(() => {
    if (placeholderNames.includes('materials')) return 'materials';
    if (placeholderNames.includes('objectives')) return 'objectives';
    return null;
  }, [placeholderNames]);

  // Get the effective grade and subject for standards section
  const gradeLevel = sections.grade_level || sections.gradeLevel || '';
  const subject = sections.subject || '';

  // Render all blocks — header is always first, then body from template
  let standardsInserted = false;
  const renderedBlocks = [];

  // Fixed header: title + grade/duration/subject grid (always rendered)
  const headerMetaFields = HEADER_PLACEHOLDERS.filter(p => p.name !== 'title');
  renderedBlocks.push(
    <div key="header" className="document-header">
      <h1 className="document-title">
        <EditableText
          value={sections.title || ''}
          onChange={(v) => handleContentChange('title', v)}
          placeholder="Lesson Title"
          className={`title-input${validationErrors.title ? ' field-error' : ''}`}
        />
      </h1>
      <div className="header-grid">
        {headerMetaFields.map(field => {
          const label = field.name === 'grade_level' ? 'Grade'
            : field.name === 'duration' ? 'Duration'
            : field.name.charAt(0).toUpperCase() + field.name.slice(1).replace(/_/g, ' ');
          const val = sections[field.name];
          return (
            <div key={field.name} className="header-item">
              <label>{label}</label>
              <EditableText
                value={val != null ? String(val) : ''}
                onChange={(v) => handleContentChange(field.name, v)}
                placeholder={label}
                className={validationErrors[field.name] ? 'field-error' : ''}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  // Body sections from template
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];

    if (block.lineType === 'h1') {
      // Skip — header is already rendered above
      continue;
    } else if (block.lineType === 'inline') {
      // Skip inline lines that only contain header fields
      const placeholderSegments = block.segments.filter(s => s.type === 'placeholder');
      const HEADER_NAMES = new Set(HEADER_PLACEHOLDERS.map(p => p.name));
      const allHeader = placeholderSegments.length > 0 &&
        placeholderSegments.every(s => HEADER_NAMES.has(s.placeholder.name));
      if (allHeader) continue;

      renderedBlocks.push(
        <p key={`block-${i}`} className="meta-line">
          {block.segments.map((seg, j) => renderInlineSegment(seg, `seg-${i}-${j}`))}
        </p>
      );
    } else if (block.lineType === 'h2') {
      renderedBlocks.push(
        <h3 key={`block-${i}`} className="section-title">
          {renderInlineMarkdown(block.text)}
        </h3>
      );
    } else if (block.lineType === 'h3') {
      renderedBlocks.push(
        <h4 key={`block-${i}`} className="section-title">
          {renderInlineMarkdown(block.text)}
        </h4>
      );
    } else if (block.lineType === 'section-body') {
      // Single placeholder that is a list/text section
      const ph = block.segments[0].placeholder;
      renderedBlocks.push(renderPlaceholder(ph, `block-${i}`));

      // Insert standards after target
      if (!standardsInserted && ph.name === standardsInsertAfter) {
        standardsInserted = true;
        renderedBlocks.push(
          <StandardsSection
            key="standards"
            lessonId={lessonId}
            standards={lessonStandards}
            onAdd={onAddStandard}
            onRemove={onRemoveStandard}
            gradeLevel={gradeLevel}
            subject={subject}
          />
        );
      }
    }
  }

  // If standards weren't inserted yet, add at end
  if (!standardsInserted) {
    renderedBlocks.push(
      <StandardsSection
        key="standards"
        lessonId={lessonId}
        standards={lessonStandards}
        onAdd={onAddStandard}
        onRemove={onRemoveStandard}
        gradeLevel={gradeLevel}
        subject={subject}
      />
    );
  }

  return (
    <div className="lesson-plan-document" ref={documentRef}>
      {renderedBlocks}
    </div>
  );
};

export default LessonPlanDocument;
